import { type Editor, useEditorState } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ArrowUpToLine,
  FoldVertical,
  ArrowDownToLine,
} from 'lucide-react';
import type { BlockVAlign } from '@jobos/shared';

const H_ALIGN_ICON = { left: AlignLeft, center: AlignCenter, right: AlignRight } as const;
const V_ALIGN_ICON = { top: ArrowUpToLine, middle: FoldVertical, bottom: ArrowDownToLine } as const;

// Standard point sizes, mirroring a typical word processor's size menu.
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72];
// Body default when a block has no explicit size — must match the CSS base size
// in both the editor (.ins-rt p) and the PDF generator (.ins-p).
const DEFAULT_BODY_PT = 12;

// Formatting controls for the focused inline editor. Lives in the side inspector.
// Constrained to the agreed set: B/I/U, horizontal + vertical alignment, font size.
export function RichTextToolbar({
  editor,
  vAlign = 'top',
  onVAlignChange,
}: {
  editor: Editor;
  vAlign?: BlockVAlign;
  onVAlignChange?: (v: BlockVAlign) => void;
}) {
  // Re-render on selection/attr changes so active states + the size value track.
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      align: (['left', 'center', 'right'] as const).find((a) => e.isActive({ textAlign: a })) ?? 'left',
      fontSize: (e.getAttributes('paragraph').fontSize ?? '') as number | '',
    }),
  });

  const btn = (active: boolean) =>
    `px-2 py-1 text-sm rounded border ${active ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'}`;

  const setFontSize = (size: number) => {
    editor.chain().focus().updateAttributes('paragraph', { fontSize: size }).run();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        <button className={btn(state.bold)} title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></button>
        <button className={btn(state.italic)} title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></button>
        <button className={btn(state.underline)} title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleUnderline().run()}><Underline className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-1">
        {(['left', 'center', 'right'] as const).map((a) => {
          const Icon = H_ALIGN_ICON[a];
          return (
            <button
              key={a}
              className={btn(state.align === a)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().setTextAlign(a).run()}
              title={`Align ${a}`}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
      {onVAlignChange && (
        <div className="flex gap-1">
          {(['top', 'middle', 'bottom'] as const).map((v) => {
            const Icon = V_ALIGN_ICON[v];
            return (
              <button
                key={v}
                className={btn(vAlign === v)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onVAlignChange(v)}
                title={`Vertical align ${v}`}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700">
        Size
        <select
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          value={state.fontSize || DEFAULT_BODY_PT}
          onChange={(e) => setFontSize(Number(e.target.value))}
        >
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>
              {s} pt
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
